import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SeverityBadge from "@/components/SeverityBadge";
import {
  Ship, MapPinned, Boxes, Users, ListChecks, AlertOctagon, AlertTriangle, Info, ArrowUpRight,
} from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.dashboard().then(setData).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-500 font-mono text-xs">Loading operations console…</div>;

  const kpis = [
    { label: "Terminals", value: data.kpi.terminals, icon: MapPinned },
    { label: "Berths", value: data.kpi.berths, icon: MapPinned },
    { label: "Vessels", value: data.kpi.vessels, icon: Ship },
    { label: "Port calls", value: data.kpi.port_calls_total, icon: ListChecks },
    { label: "Terminal assets", value: data.kpi.assets, icon: Boxes },
    { label: "Personnel", value: data.kpi.personnel, icon: Users },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Operations Console</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-slate-50">Today's Port Operations</h1>
        </div>
        <Link
          to="/port-calls"
          data-testid="dashboard-go-port-calls"
          className="flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 font-mono uppercase tracking-widest"
        >
          View all <ArrowUpRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid={"dashboard-kpi"}>
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="panel p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{k.label}</span>
                <Icon size={14} className="text-sky-400" strokeWidth={1.5} />
              </div>
              <div className="font-heading text-2xl font-black tracking-tighter text-slate-50">{k.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 panel p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Upcoming Port Calls</div>
              <div className="font-heading text-xl font-bold text-slate-100">{data.kpi.port_calls_upcoming} scheduled</div>
            </div>
          </div>
          <div className="space-y-2">
            {data.upcoming.length === 0 && <div className="text-sm text-slate-500">No upcoming port calls.</div>}
            {data.upcoming.map((p) => (
              <Link
                key={p.id}
                to={`/port-calls/${p.id}`}
                data-testid={`dashboard-pc-${p.id}`}
                className="block panel-2 hover:bg-[#1E2B41] p-3 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Ship size={16} className="text-sky-400" />
                    <div>
                      <div className="text-sm font-medium text-slate-100">{p.vessel_id}</div>
                      <div className="font-mono text-[11px] text-slate-500">
                        ETA {p.eta?.slice(0, 16).replace("T", " ")} · ETD {p.etd?.slice(0, 16).replace("T", " ")}
                      </div>
                    </div>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                    p.status === "approved" ? "sev-ok" : p.status === "submitted" ? "sev-info" : "sev-warning"
                  }`}>
                    {p.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="panel p-5" data-testid={"dashboard-conflicts-badge"}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Active Conflicts</div>
            <div className="font-heading text-4xl font-black tracking-tighter text-slate-50 mb-3">{data.conflicts_total}</div>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center justify-between"><span className="flex items-center gap-2"><AlertOctagon size={12} className="text-red-400" /> Blocking</span><span className="font-mono">{data.severity_breakdown.blocking || 0}</span></div>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2"><AlertOctagon size={12} className="text-red-400" /> Critical</span><span className="font-mono">{data.severity_breakdown.critical || 0}</span></div>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2"><AlertTriangle size={12} className="text-amber-400" /> Warning</span><span className="font-mono">{data.severity_breakdown.warning || 0}</span></div>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2"><Info size={12} className="text-sky-400" /> Info</span><span className="font-mono">{data.severity_breakdown.info || 0}</span></div>
            </div>
          </div>
          <div className="panel p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Status Breakdown</div>
            <div className="space-y-1.5 text-xs">
              {Object.entries(data.by_status).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="capitalize">{k}</span>
                  <span className="font-mono text-slate-400">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
